/**
 * 暖棕摩卡模版独立导航栏
 *
 * 设计理念：咖啡馆质感，商务杂志目录风
 * - 暖米白实底 + 顶部 4px 深棕粗条 + 底部 1px 焦糖细线
 * - 导航居中，菜单项以竖线分隔
 * - 深棕发布按钮为唯一 CTA
 */

import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function MochaNavbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [logo, setLogo] = useState("");
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoggedIn, isAdmin, logout } = useAuth();
  const { siteName: contextSiteName } = useSite();
  const { siteConfig, navMenus, navCategories } = useSiteConfig();
  const enableVip = siteConfig?.enable_vip ?? true;

  const siteName = contextSiteName;

  useEffect(() => {
    if (!siteConfig) return;
    if (siteConfig.logo) {
      setLogo(siteConfig.logo);
      setLogoError(false);
    }
  }, [siteConfig]);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate("/");
  };

  const isHome = location.pathname === "/";

  const isActiveMenu = (path: string) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FAF6F0] border-b border-[#C77B3F]/25 shadow-sm">
      {/* 顶部深棕粗条 */}
      <div className="h-1 bg-[#4A3525]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14 md:h-16">

          {/* ── 左侧：Logo + 站点名 ── */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            {logo && !logoError ? (
              <img
                src={logo}
                alt={siteName}
                className="h-9 md:h-10 w-auto object-contain"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="h-9 w-9 rounded-md bg-[#4A3525] flex items-center justify-center text-[#DDC7A0] font-bold text-sm">
                {siteName ? siteName.charAt(0).toUpperCase() : 'S'}
              </div>
            )}
            {siteName && (
              <span className="text-base md:text-lg font-bold text-[#2B241D] tracking-tight">
                {siteName}
              </span>
            )}
          </Link>

          {/* ── 中间：主导航（桌面端，竖线分隔） ── */}
          <div className="hidden lg:flex items-center">
            <Link
              to="/"
              className={`px-3 py-1.5 text-sm font-medium transition-colors border-r border-[#E8DCC8] ${
                isHome
                  ? "text-[#C77B3F] font-bold"
                  : "text-[#4A3525] hover:text-[#C77B3F]"
              }`}
            >
              首页
            </Link>
            {navMenus.map((menu) => (
              <Link
                key={menu.id}
                to={menu.type === 'category' ? `/category/${menu.target_url || ''}` :
                     menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                     menu.type === 'link' ? (menu.target_url || '#') : '#'}
                className={`px-3 py-1.5 text-sm font-medium transition-colors border-r border-[#E8DCC8] ${
                  isActiveMenu(menu.type === 'category' ? `/category/${menu.target_url || ''}` : menu.type === 'page' ? `/page/${menu.target_url || ''}` : '')
                    ? "text-[#C77B3F] font-bold"
                    : "text-[#4A3525] hover:text-[#C77B3F]"
                }`}
              >
                {menu.name}
              </Link>
            ))}
            {navCategories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-3 py-1.5 text-sm font-medium transition-colors border-r border-[#E8DCC8] ${
                  location.pathname === `/category/${cat.slug}`
                    ? "text-[#C77B3F] font-bold"
                    : "text-[#4A3525] hover:text-[#C77B3F]"
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* ── 右侧：用户区 + 发布按钮 ── */}
          <div className="flex items-center gap-2">
            {isLoggedIn ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-[#4A3525]/5 transition-colors"
                >
                  <div className="w-7 h-7 rounded-full bg-[#4A3525]/10 flex items-center justify-center text-[#4A3525] text-xs font-bold overflow-hidden">
                    {user?.avatar ? (
                      <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                    ) : (
                      (user?.nickname || 'U').charAt(0).toUpperCase()
                    )}
                  </div>
                  <span className="text-sm text-[#4A3525] hidden md:inline max-w-[80px] truncate">
                    {user?.nickname}
                  </span>
                  <i className="ri-arrow-down-s-line text-[#8A7B6D] text-xs" />
                </button>

                {isUserMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setIsUserMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-1.5 w-44 bg-white rounded-lg shadow-lg border border-[#E8DCC8] py-1.5 z-20">
                      <Link
                        to="/dashboard"
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#4A3525] hover:bg-[#FAF6F0] hover:text-[#C77B3F] transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <i className="ri-user-line" />会员中心
                      </Link>
                      <Link
                        to="/dashboard/my-listings"
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#4A3525] hover:bg-[#FAF6F0] hover:text-[#C77B3F] transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <i className="ri-file-list-line" />我的发布
                      </Link>
                      {enableVip && (
                        <Link
                          to="/vip"
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#C77B3F] font-medium hover:bg-[#FAF6F0] transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <i className="ri-vip-crown-line" />开通VIP
                        </Link>
                      )}
                      {isAdmin && (
                        <>
                          <div className="border-t border-[#F0E6D6] my-1" />
                          <Link
                            to="/admin"
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#4A3525] hover:bg-[#FAF6F0] hover:text-[#C77B3F] transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <i className="ri-shield-star-line" />后台管理
                          </Link>
                        </>
                      )}
                      <div className="border-t border-[#F0E6D6] my-1" />
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#8A7B6D] hover:bg-gray-50 hover:text-[#C77B3F] transition-colors w-full text-left"
                      >
                        <i className="ri-logout-box-line" />退出登录
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={() => navigate("/login")}
                className="hidden sm:inline-flex items-center gap-1 px-3.5 py-2 text-sm font-medium text-[#4A3525] rounded-lg hover:bg-[#4A3525]/5 transition-colors"
              >
                <i className="ri-user-line" />
                <span>登录</span>
              </button>
            )}

            <button
              onClick={() => navigate("/publish")}
              className="inline-flex items-center gap-1 px-3.5 py-2 text-sm font-bold bg-[#4A3525] text-[#FAF6F0] rounded-md hover:bg-[#3A2A1D] transition-colors shadow-sm border border-[#C77B3F]/40"
            >
              <i className="ri-add-line text-base" />
              <span className="hidden sm:inline">立即发布</span>
            </button>

            {/* 移动端菜单按钮 */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-1.5 rounded-lg hover:bg-[#4A3525]/5 transition-colors"
            >
              <i className={`ri-${isMobileMenuOpen ? "close" : "menu"}-line text-xl text-[#4A3525]`} />
            </button>
          </div>
        </div>
      </div>

      {/* ── 移动端菜单 ── */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-[#E8DCC8] bg-[#FAF6F0]">
          <div className="px-4 py-2 space-y-0.5 max-h-[60vh] overflow-y-auto">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                isHome ? "text-[#C77B3F] bg-[#4A3525]/5 font-bold" : "text-[#4A3525] hover:bg-[#4A3525]/5"
              }`}
            >
              <i className="ri-home-4-line mr-2" />首页
            </Link>
            {navMenus.map((menu) => (
              <Link
                key={menu.id}
                to={menu.type === 'category' ? `/category/${menu.target_url || ''}` :
                     menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                     menu.type === 'link' ? (menu.target_url || '#') : '#'}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-[#4A3525] rounded-lg hover:bg-[#4A3525]/5 transition-colors"
              >
                <i className="ri-menu-line mr-2" />{menu.name}
              </Link>
            ))}
            {navCategories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                  location.pathname === `/category/${cat.slug}` ? "text-[#C77B3F] bg-[#4A3525]/5 font-bold" : "text-[#4A3525] hover:bg-[#4A3525]/5"
                }`}
              >
                <i className="ri-grid-line mr-2" />{cat.name}
              </Link>
            ))}

            {/* VIP */}
            {enableVip && (
              <Link
                to="/vip"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-[#C77B3F] rounded-lg hover:bg-[#4A3525]/5 transition-colors"
              >
                <i className="ri-vip-crown-line mr-2" />开通VIP
              </Link>
            )}

            <div className="border-t border-[#E8DCC8] my-1" />

            {isLoggedIn ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2.5 text-sm font-medium text-[#4A3525] rounded-lg hover:bg-[#4A3525]/5 transition-colors"
                >
                  <i className="ri-user-line mr-2" />会员中心
                </Link>
                <Link
                  to="/dashboard/my-listings"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2.5 text-sm font-medium text-[#4A3525] rounded-lg hover:bg-[#4A3525]/5 transition-colors"
                >
                  <i className="ri-file-list-line mr-2" />我的发布
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-3 py-2.5 text-sm font-medium text-[#4A3525] rounded-lg hover:bg-[#4A3525]/5 transition-colors"
                  >
                    <i className="ri-shield-star-line mr-2" />后台管理
                  </Link>
                )}
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2.5 text-sm font-medium text-[#8A7B6D] rounded-lg hover:bg-[#4A3525]/5 transition-colors"
                >
                  <i className="ri-logout-box-line mr-2" />退出登录
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-[#C77B3F] rounded-lg hover:bg-[#4A3525]/5 transition-colors"
              >
                <i className="ri-user-line mr-2" />登录 / 注册
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
